// src/components/projectleiders/SprintTab.jsx
import React, { useMemo } from "react";
import { projectleidersCatalog } from "../../content/projectleiders.catalog";
import ActionChecklist from "../shared/ActionChecklist";
import UpdateSection from "../shared/UpdateSection";

// ─── helpers ────────────────────────────────────────────────────────────────

function san(str) {
  return String(str).replace(/\s+/g, "_");
}

function makeActionKey(domain, sprintId, deliverableId, actieId) {
  return `${san(domain)}_${sprintId}_${san(deliverableId)}_${san(actieId)}`;
}

function makeUpdateKey(domain, sprintId, deliverableId) {
  return `${san(domain)}_${sprintId}_${san(deliverableId)}`;
}

function pct(done, total) {
  if (!total) return 0;
  return Math.round((done / total) * 100);
}

// ─── Werkinstructie bullets ──────────────────────────────────────────────────

function WerkinstructieBullets({ text }) {
  const sentences = text
    .split(/(?<=\.)\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  return (
    <ul className="text-[12px] text-slate-700 space-y-1 mb-1">
      {sentences.map((s, i) => (
        <li key={i} className="flex gap-1">
          <span className="mt-[3px] text-[10px] text-sky-500">●</span>
          <span>{s.endsWith(".") ? s : s + "."}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Deliverable card (sprint view) ─────────────────────────────────────────

function DeliverableCard({
  deliverable,
  sprintId,
  domain,
  checkedActions,
  updates,
  userEmail,
  onToggle,
  onSaveUpdate,
  onSaveDashboard,
  onDeleteUpdate,
}) {
  const actions = deliverable.actiepunten || [];
  const actionKeys = actions.map((a) =>
    makeActionKey(domain, sprintId, deliverable.id, a.id)
  );
  const updateKey = makeUpdateKey(domain, sprintId, deliverable.id);

  return (
    <div className="card-3d rounded-2xl p-4 flex flex-col gap-3">
      {/* Title & icon */}
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-xl"
          style={{
            width: 40,
            height: 40,
            background: "linear-gradient(145deg,#eff6ff,#dbeafe)",
            boxShadow:
              "0 4px 10px rgba(37,99,235,0.25), inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <span className="text-xl">{deliverable.icon}</span>
        </div>
        <div className="text-slate-900 text-xl font-extrabold leading-tight">
          {deliverable.title}
        </div>
      </div>

      {/* Werkinstructie */}
      <div>
        <div className="section-label">Werkinstructie</div>
        <WerkinstructieBullets text={deliverable.werkinstructie || ""} />
      </div>

      {/* Actiepunten */}
      <ActionChecklist
        actions={actions}
        actionKeys={actionKeys}
        checkedActions={checkedActions}
        onToggle={onToggle}
      />

      {/* Update */}
      <UpdateSection
        updateKey={updateKey}
        updates={updates}
        userEmail={userEmail}
        onSaveUpdate={onSaveUpdate}
        onSaveDashboard={onSaveDashboard}
        onDeleteUpdate={onDeleteUpdate}
      />
    </div>
  );
}

// ─── Sprint overview card (read-only, per sprint) ────────────────────────────

function SprintOverviewCard({ sprint, domain, checkedActions }) {
  const deliverables = sprint.deliverables;

  const deliverableProgress = useMemo(() =>
    deliverables.map((d) => {
      const actions = d.actiepunten || [];
      const total = actions.length;
      const done = actions.filter((a) =>
        checkedActions[makeActionKey(domain, sprint.id, d.id, a.id)]
      ).length;
      return { deliverable: d, done, total, ratio: pct(done, total) };
    }),
    [deliverables, domain, sprint.id, checkedActions]
  );

  const totalActions = deliverableProgress.reduce((sum, dp) => sum + dp.total, 0);
  const totalDone = deliverableProgress.reduce((sum, dp) => sum + dp.done, 0);
  const totalRatio = pct(totalDone, totalActions);

  const sprintShortLabel = sprint.label.split("—")[0].trim();
  const sprintNumber = sprintShortLabel.replace("Sprint ", "");

  return (
    <div className="card-3d rounded-2xl p-4 flex flex-col gap-3">
      {/* Title & icon */}
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-xl"
          style={{
            width: 40,
            height: 40,
            background: "linear-gradient(145deg,#eff6ff,#dbeafe)",
            boxShadow:
              "0 4px 10px rgba(37,99,235,0.25), inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <span className="text-base font-extrabold" style={{ color: "#27435d" }}>
            {sprintNumber}
          </span>
        </div>
        <div className="text-slate-900 text-xl font-extrabold leading-tight">
          {sprint.label}
        </div>
      </div>

      {/* Per-deliverable progress rows */}
      <div className="flex flex-col gap-2">
        {deliverableProgress.map(({ deliverable, ratio }) => (
          <div key={deliverable.id} className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium text-slate-700 flex-1 min-w-0 truncate">
              {deliverable.title}
            </span>
            <span
              className="flex-shrink-0"
              style={{
                background: "#31719D",
                color: "#ffffff",
                borderRadius: "999px",
                padding: "1px 7px",
                fontWeight: 800,
                fontSize: "11px",
              }}
            >
              {ratio}%
            </span>
          </div>
        ))}
      </div>

      {/* Total row */}
      <div
        className="flex items-center justify-between gap-2 pt-2 border-t"
        style={{ borderColor: "#e2eaf3" }}
      >
        <span className="text-sm font-semibold text-slate-700">Totaal</span>
        <span
          style={{
            background: "#31719D",
            color: "#ffffff",
            borderRadius: "999px",
            padding: "1px 9px",
            fontWeight: 800,
            fontSize: "11px",
          }}
        >
          {totalRatio}%
        </span>
      </div>
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function SprintTab({
  sprintId,
  domain,
  checkedActions,
  updates,
  userEmail,
  onToggle,
  onSaveUpdate,
  onSaveDashboard,
  onDeleteUpdate,
}) {
  const isOverview = sprintId === "overview";

  // For sprint view: find sprint in catalog
  const sprint = useMemo(
    () => projectleidersCatalog.sprints.find((s) => s.id === sprintId) ?? null,
    [sprintId]
  );

  // Aggregate progress for the tab header pill
  const { totalActions, doneActions } = useMemo(() => {
    let total = 0;
    let done = 0;

    if (isOverview) {
      for (const s of projectleidersCatalog.sprints) {
        for (const d of s.deliverables) {
          for (const a of d.actiepunten || []) {
            const key = makeActionKey(domain, s.id, d.id, a.id);
            total++;
            if (checkedActions[key]) done++;
          }
        }
      }
    } else if (sprint) {
      for (const d of sprint.deliverables) {
        for (const a of d.actiepunten || []) {
          const key = makeActionKey(domain, sprint.id, d.id, a.id);
          total++;
          if (checkedActions[key]) done++;
        }
      }
    }

    return { totalActions: total, doneActions: done };
  }, [isOverview, sprint, domain, checkedActions]);

  const overallPct = pct(doneActions, totalActions);
  const sprintLabel = sprint ? sprint.label.split("—")[0].trim() : "Totaaloverzicht";

  return (
    <div className="space-y-4">
      {/* Tab header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: "#ffffff" }}
          >
            {isOverview ? "Totaaloverzicht" : sprintLabel}
          </div>
          <div className="text-sm text-slate-200">
            {isOverview
              ? "Overzicht van alle deliverables en voortgang per domein."
              : "Werk de deliverables af en geef updates over de voortgang van deze sprint."}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div
            className="flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold"
            style={{
              background: "#ffffff",
              color: "#27435d",
              boxShadow: "0 3px 10px rgba(0,0,0,0.22)",
              letterSpacing: "0.01em",
            }}
          >
            <span>
              Gemiddelde voortgang {isOverview ? "Totaal" : sprintLabel}
            </span>
            <span
              style={{
                background: "#31719D",
                color: "#ffffff",
                borderRadius: "999px",
                padding: "1px 9px",
                fontWeight: 800,
                fontSize: "11px",
                letterSpacing: "0.03em",
              }}
            >
              {overallPct}%
            </span>
          </div>
        </div>
      </div>

      {/* Deliverable grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {isOverview
          ? projectleidersCatalog.sprints.map((s) => (
              <SprintOverviewCard
                key={s.id}
                sprint={s}
                domain={domain}
                checkedActions={checkedActions}
              />
            ))
          : (sprint?.deliverables ?? []).map((deliverable) => (
              <DeliverableCard
                key={deliverable.id}
                deliverable={deliverable}
                sprintId={sprint.id}
                domain={domain}
                checkedActions={checkedActions}
                updates={updates}
                userEmail={userEmail}
                onToggle={onToggle}
                onSaveUpdate={onSaveUpdate}
                onSaveDashboard={onSaveDashboard}
                onDeleteUpdate={onDeleteUpdate}
              />
            ))}
      </div>
    </div>
  );
}
